import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Plus } from 'lucide-react'
import AvailabilityForm from './AvailabilityForm'
import { useCenterStore, useOrganisationStore, useAvailabilityStore } from '@caradasstore'
import { getDateOverrides, deactivateDateOverridesByDate } from '../../../api/api'
import { Logger } from '../../../logger'
import { groupSlotsBySameDate } from '../../../utils/utils'
import dayjs from 'dayjs'

const formatTimeSlot = (time) => dayjs(time, 'HH:mm').format('h:mm A')

const Row = ({ date, slots, label, onEdit, onRemove }) => {
  const dateLabel = dayjs(date).format('ddd, MMMM D, YYYY')
  const timeText =
    !slots || slots.length === 0
      ? 'All day · Unavailable'
      : slots.map((s) => `${formatTimeSlot(s.start)} – ${formatTimeSlot(s.end)}`).join(', ')
  return (
    <div className='grid grid-cols-[1fr_2fr_1fr_auto] items-center gap-4 px-4 py-3'>
      <div className='text-[13px] font-medium text-[#0A1628]'>{dateLabel}</div>
      <div className='text-[13px] text-[#475569]'>{timeText}</div>
      <div className='text-right text-[13px] text-[#475569]'>{label || ''}</div>
      <div className='flex items-center gap-3 justify-end'>
        <button type='button' onClick={onEdit} className='text-[13px] font-semibold text-[#0A1628] hover:underline'>
          Edit
        </button>
        <button type='button' onClick={onRemove} className='text-[13px] font-semibold text-[#DC2626] hover:underline'>
          Remove
        </button>
      </div>
    </div>
  )
}

Row.propTypes = {
  date: PropTypes.string,
  slots: PropTypes.array,
  label: PropTypes.string,
  onEdit: PropTypes.func,
  onRemove: PropTypes.func
}

const DateOverrides = () => {
  const [dates, setDates] = useState([])
  const [isAddDateOpen, setIsAddDateOpen] = useState(false)
  const [editingIndex, setEditingIndex] = useState(null)
  const ccId = useCenterStore((store) => store.ccId)
  const organisationId = useOrganisationStore((store) => store.organisationId)
  const setAvailabilityDetail = useAvailabilityStore((store) => store.setAvailability)
  const [loading, setLoading] = useState(false)

  const handleRemove = async (index) => {
    const dateToRemove = dates[index]?.overrides_date
    if (!dateToRemove) return
    try {
      await deactivateDateOverridesByDate({ ccId, organisation_id: organisationId, overrides_date: dateToRemove })
      await fetchDateOverrides()
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'dateoverrides', method: 'handleRemove' } })
    }
  }

  const fetchDateOverrides = async () => {
    try {
      setLoading(true)
      const response = await getDateOverrides({ ccId, organisation_id: organisationId })
      if (response) {
        const dateWithSlots = groupSlotsBySameDate(response.data)
        setDates(dateWithSlots)
        setAvailabilityDetail(dateWithSlots)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'dateoverrides', method: 'fetchDateOverrides' } })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if ((ccId && organisationId) || !isAddDateOpen) fetchDateOverrides()
  }, [ccId, organisationId, isAddDateOpen])

  const openAdd = () => {
    setEditingIndex(null)
    setIsAddDateOpen(true)
  }

  const openEdit = (index) => {
    setEditingIndex(index)
    setIsAddDateOpen(true)
  }

  const closePanel = () => {
    setIsAddDateOpen(false)
    setEditingIndex(null)
  }

  return (
    <div className='flex flex-col lg:flex-row gap-4 items-start'>
      <div className='flex-1 min-w-0 w-full'>
        <div className='mb-3 flex flex-wrap items-start justify-between gap-3'>
          <div>
            <h2 className='text-[18px] font-bold text-[#0A1628]'>Date Overrides</h2>
            <p className='mt-0.5 text-[13px] text-[#6B7280]'>
              Use these to change availability for specific dates, overriding the weekly schedule.
            </p>
          </div>
          <button
            type='button'
            onClick={openAdd}
            className='inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#D1D5D8] bg-white text-[13px] font-medium text-[#0A1628] hover:bg-[#F8FAFC] transition-colors'
          >
            <Plus className='h-4 w-4' />
            Add Date
          </button>
        </div>

        <div className='rounded-xl border border-[#EBEDF0] bg-white'>
          {loading ? (
            <div className='px-4 py-6 text-center text-[13px] text-[#6B7280]'>Loading date overrides...</div>
          ) : dates.length === 0 ? (
            <div className='px-4 py-6 text-center text-[13px] text-[#6B7280]'>No date overrides available</div>
          ) : (
            dates.map((d, index) => (
              <div key={d.overrides_date || index} className={index === dates.length - 1 ? '' : 'border-b border-[#EBEDF0]'}>
                <Row
                  date={d.overrides_date || ''}
                  slots={d.slots || []}
                  label={d.label || ''}
                  onEdit={() => openEdit(index)}
                  onRemove={() => handleRemove(index)}
                />
              </div>
            ))
          )}
        </div>
      </div>

      {isAddDateOpen && (
        <aside className='w-full lg:w-[420px] lg:flex-none rounded-xl border border-[#EBEDF0] bg-white shadow-sm overflow-hidden self-stretch'>
          <div className='max-h-[calc(100vh-260px)] overflow-y-auto'>
            <AvailabilityForm
              setIsAddDateOpen={closePanel}
              dates={dates}
              onSuccess={fetchDateOverrides}
              editingOverride={editingIndex != null ? dates[editingIndex] : null}
            />
          </div>
        </aside>
      )}
    </div>
  )
}

export default DateOverrides
